import { Capacitor } from '@capacitor/core';
import { TextZoom } from '@capacitor/text-zoom';
import { Platform } from '@ionic/angular/standalone';
import { FeatureFlagsService } from '@wilson/feature-flags';
import { LaunchDarklyUserKeys } from '@wilson/interfaces';
import { SelectLanguage } from '@wilson/localization';
import type { LanguagePreferenceStorage } from '@wilson/localization';
import { LDOptions } from 'launchdarkly-js-client-sdk';
import { StorageService } from './services/storage.service';
import { MobileLanguagePreferenceStorage } from './services/mobile-language-preference-storage.service';
import { Store } from '@ngxs/store';

/**
 * Preload stored language into the preference storage cache so that when
 * LocalizationState.ngxsOnInit may already have run with a system/default language
 * before the async preference is known. Once preload completes and the preferred
 * language is available, we dispatch SelectLanguage to correct any earlier choice.
 */
async function initLanguagesAndI18N(
  store: Store,
  preferenceStorage: LanguagePreferenceStorage,
): Promise<void> {
  if (preferenceStorage instanceof MobileLanguagePreferenceStorage) {
    await preferenceStorage.preload();
    const preferred = preferenceStorage.getUserPreferredLanguage();
    if (preferred) {
      // Dispatch a second SelectLanguage here because LocalizationState.ngxsOnInit
      // might have already chosen a system/default language before the async
      // preference from Capacitor storage was loaded. This ensures the app
      // converges to the user's saved language once it is known.
      store.dispatch(new SelectLanguage(preferred));
    }
  }
}

async function initFeatureFlags(
  featureFlags: FeatureFlagsService,
  storage: StorageService,
): Promise<void> {
  const storedFlags = await storage.persistent('featureFlags');
  const hasStoredFlags = storedFlags && Object.keys(storedFlags).length;
  const options: LDOptions | undefined = hasStoredFlags
    ? { bootstrap: storedFlags }
    : undefined;

  featureFlags.initialize(
    LaunchDarklyUserKeys.WilsonMobile,
    undefined,
    options,
  );
}

/**
 * prepares libraries and data required before the app is being started
 */
async function prepareApp({
  store,
  featureFlags,
  platform,
  storage,
  preferenceStorage,
}: {
  store: Store;
  featureFlags: FeatureFlagsService;
  platform: Platform;
  storage: StorageService;
  preferenceStorage: LanguagePreferenceStorage;
}): Promise<void> {
  // feature flags come first
  await initFeatureFlags(featureFlags, storage);

  // preload language preference and correct language once async preference is known
  await initLanguagesAndI18N(store, preferenceStorage);

  // similar or same as dom-ready
  await platform.ready();

  if (Capacitor.isNativePlatform()) {
    // set text-zoom to 1.0, ignore preferred textZoom setting (android automatically uses this)
    await TextZoom.set({ value: 1.0 });
  }
}

export function appInitializerFactory(
  store: Store,
  featureFlags: FeatureFlagsService,
  platform: Platform,
  storage: StorageService,
  preferenceStorage: LanguagePreferenceStorage,
): () => Promise<void> {
  return async () => {
    await prepareApp({
      store,
      featureFlags,
      platform,
      storage,
      preferenceStorage,
    });
  };
}
